/**
 * Canonical AI Entity Model
 * 
 * This is the single source of truth for AI profiles.
 * Replaces the split between Profile and AiProfile.
 * 
 * All AI entities must conform to this schema.
 */

import type { ModePreset, PrivacyMode, BrandConfig, Sliders } from "./ai";

/**
 * AI Entity - The canonical model for all AI instances
 */
export interface AIEntity {
  // Identity
  id: string;
  name: string;
  description?: string;
  
  // Ownership
  ownerId: string; // Required - must be real userId, never "default"
  createdAt: number;
  updatedAt: number;
  
  // Configuration
  modePreset: ModePreset;
  brand: BrandConfig;
  tiles: string[]; // max 6
  sliders: Sliders;
  privacyMode: PrivacyMode;
  agentModeEnabled: boolean;
  
  // Lifecycle
  version: number;
  parentEntityId?: string; // If forked/cloned
  templateId?: string; // ID of template preset used (if any)
  
  // Sharing & Publishing
  isPublic: boolean;
  shareSlug?: string;
  customDomain?: string;
  publishedAt?: number;
  
  // Analytics
  viewCount: number;
  cloneCount: number;
  
  // Collaboration
  teamId?: string;
  
  // Customization
  customCSS?: string;
  customTheme?: Record<string, any>;
  customInstructions?: string; // Primary purpose / role (used in system prompt)
  
  // Subscription
  plan: "free" | "pro" | "pro_plus" | "enterprise";
  
  // Metadata
  lastActivityAt?: number; // Last time user interacted with this AI
  conversationCount?: number; // Number of conversations
  messageCount?: number; // Total messages across all conversations
  // Power-ups (OpenClaw/Replit-style)
  capabilities?: {
    channels?: { enabled: boolean; selected?: string[]; webhookBaseUrl?: string };
    actions?: { enabled: boolean; allowed?: string[] };
    memory?: { enabled: boolean };
    skills?: { enabled: boolean; skillIds?: string[] };
    workflows?: { enabled: boolean };
    deploy?: { enabled: boolean; provider?: string };
    replitAgent?: { enabled: boolean };
  };
}

/**
 * Convert legacy AiProfile to AIEntity
 */
export function aiProfileToEntity(profile: any): AIEntity {
  return {
    id: profile.id,
    name: profile.name,
    description: profile.description,
    ownerId: profile.ownerId,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    modePreset: profile.modePreset,
    brand: profile.brand,
    tiles: profile.tiles || [],
    sliders: profile.sliders,
    privacyMode: profile.privacyMode,
    agentModeEnabled: profile.agentModeEnabled || false,
    version: profile.version || 1,
    parentEntityId: profile.parentProfileId,
    templateId: profile.templateId,
    isPublic: profile.isPublic || false,
    shareSlug: profile.shareSlug,
    customDomain: profile.customDomain,
    publishedAt: profile.publishedAt,
    viewCount: profile.viewCount || 0,
    cloneCount: profile.cloneCount || 0,
    teamId: profile.teamId,
    customCSS: profile.customCSS,
    customTheme: profile.customTheme,
    customInstructions: profile.customInstructions,
    plan: profile.plan || "free",
    lastActivityAt: profile.lastActivityAt,
    conversationCount: profile.conversationCount,
    messageCount: profile.messageCount,
    capabilities: profile.capabilities,
  };
}

/**
 * Convert AIEntity to legacy AiProfile (for backward compatibility)
 */
export function entityToAiProfile(entity: AIEntity): import("./ai").AiProfile {
  const profile: import("./ai").AiProfile = {
    id: entity.id,
    name: entity.name,
    description: entity.description,
    modePreset: entity.modePreset,
    brand: entity.brand,
    tiles: entity.tiles,
    sliders: entity.sliders,
    privacyMode: entity.privacyMode,
    agentModeEnabled: entity.agentModeEnabled,
    plan: entity.plan,
    isPublic: entity.isPublic,
    shareSlug: entity.shareSlug,
    customDomain: entity.customDomain,
    version: entity.version,
    parentProfileId: entity.parentEntityId,
    viewCount: entity.viewCount,
    cloneCount: entity.cloneCount,
    teamId: entity.teamId,
    ownerId: entity.ownerId,
    customCSS: entity.customCSS,
    customTheme: entity.customTheme,
    customInstructions: entity.customInstructions,
    templateId: entity.templateId,
    capabilities: entity.capabilities,
    createdAt: entity.createdAt,
    updatedAt: entity.updatedAt,
    publishedAt: entity.publishedAt,
  };
  return profile;
}

